import { gql } from 'graphql-request';
import { PortfolioSiteSummary } from 'src/shared/types/graph/portfolio';

/** Shape passed to portfolio summary child components (composed from purpose-built queries in PortfolioSummary). */
export type PortfolioData = {
    objective: string;
    summary: PortfolioSiteSummary;
    assets: {
        filters: {
            owners: string[];
            countries: string[];
            years: number[];
            availableMetrics: string[];
        };
    };
};

// --- Purpose-built portfolio endpoints ---

export const portfolioOverviewQuery = gql`
    query getPortfolioOverview($customerId: String!, $filters: [CustomFilterOptionInput]) {
        portfolio {
            getPortfolioOverview(customerId: $customerId, filters: $filters) {
                objective
                numberSites
                totalArea
                ownerCount
                countryCount
            }
        }
    }
`;

export type PortfolioOverviewData = {
    objective: string | null;
    numberSites: number;
    totalArea: number;
    ownerCount: number;
    countryCount: number;
};

export type PortfolioOverviewQueryResult = {
    portfolio: {
        getPortfolioOverview: PortfolioOverviewData;
    };
};

export const habitatIndicatorsQuery = gql`
    query getHabitatIndicators($customerId: String!, $filters: [CustomFilterOptionInput]) {
        portfolio {
            getHabitatIndicators(customerId: $customerId, filters: $filters) {
                numberSites
                biiSiteSummary {
                    intact
                    moderate_intactness
                    low_intactness
                    severely_degraded
                }
                hmiSiteSummary {
                    very_high_modification
                    high_modification
                    medium_modification
                    low_modification
                    no_modification
                }
                metrics {
                    name
                    label
                    frameworks
                    ... on MultiYearMetricSingleYearSiteMetric {
                        years
                        metricWithTrend
                        yearCoverage {
                            year
                            assetCount
                            complete
                        }
                    }
                    ... on MultiMetricSingleYearSiteMetric {
                        year
                        metrics
                    }
                    ... on MultiYearSiteMetric {
                        years
                        datum
                    }
                    ... on SingleYearSiteMetric {
                        year
                        data
                    }
                }
            }
        }
    }
`;

export type HabitatIndicatorsData = {
    numberSites: number;
    biiSiteSummary: {
        intact: number | null;
        moderate_intactness: number | null;
        low_intactness: number | null;
        severely_degraded: number | null;
    };
    hmiSiteSummary: {
        very_high_modification: number | null;
        high_modification: number | null;
        medium_modification: number | null;
        low_modification: number | null;
        no_modification: number | null;
    };
    metrics: import('src/shared/types/graph/portfolio').PortfolioSiteMetric[];
};

export type HabitatIndicatorsQueryResult = {
    portfolio: {
        getHabitatIndicators: HabitatIndicatorsData;
    };
};

// The card shape the summary components render, mapped from the grid summary.
export type SpeciesSummaryCardsData = {
    numberSites: number;
    totalCount: number;
    redListSpecies: {
        siteCount: number;
        count: number;
        frameworks: string[];
        threatenedCount: number;
        chartData: { iucnRedListCategory: string; count: number }[];
    };
    invasiveSpecies: {
        count: number;
        siteCount: number;
        frameworks: string[];
    };
};

// Only customFilters is requested here - PortfolioFilters.tsx (the only
// consumer of this query) reads nothing else off the response. The metric
// selector's own availableMetrics comes from getPortfolioMapFilters instead
// (porfolioSiteExplorerQuery.ts), a separate query that already only asks
// for that one field. owners/countries/sites/years were removed from the
// backend schema entirely (DD-6079) - they were fetched here but never read.
export const portfolioFiltersNewQuery = gql`
    query getPortfolioFilters($customerId: String!) {
        portfolio {
            getPortfolioFilters(customerId: $customerId) {
                customFilters
            }
        }
    }
`;

export type PortfolioFiltersNewQueryResult = {
    portfolio: {
        getPortfolioFilters: {
            customFilters: { [key: string]: string[] };
        };
    };
};

export const setPortfolioObjective = gql`
    mutation setPortfolio($customerId: String!, $objective: String!) {
        portfolio {
            updatePortfolio(customerId: $customerId, objective: $objective)
        }
    }
`;
